// Adding the grammar
import "prism-react-editor/prism/languages/json";

// Adds comment toggling and auto-indenting
import "prism-react-editor/languages/json";

import "prism-react-editor/layout.css";
import "prism-react-editor/themes/github-dark.css";

// Required by the basic setup
import "prism-react-editor/search.css";

import { useMutation, useQuery } from "@tanstack/react-query";
import { Editor } from "prism-react-editor";
import { BasicSetup } from "prism-react-editor/setups";
import { useEffect, useState } from "react";
import { Alert, Button, Col, Container, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { automation_config_defaults } from "../../data/automation_config_defaults";
import { useAppSelector } from "../../store";
import { fetchMailTemplateConfig, saveMailTemplateConfig } from "../../utils/http/mail-template-configs";
import { queryClient } from "../../utils/http/queryClient";

function TemplateConfig() {
  const params = useParams();

  const activeCompany = useAppSelector((state) => state.company.activeCompany);
  const templatesMaintained = useAppSelector((state) => state.company.templateSummary.maintained);

  const { automation, event, action, target } = params;
  const mailTemplatePath = { event, action, target };

  const [saving, setSaving] = useState(false);

  const [attachFiles, setAttachFiles] = useState<boolean>(false);

  const emptyBCC = {
    extra: ["email"],
    trustpilot: "email",
    representative: true,
    office: {
      name: ["email"],
    },
  };

  const emptyFileAttachmentConfig = {
    crmFiles: [
      {
        matchPublished: true,
        matchName: ".*",
        matchType: ".*",
        nameReplacements: [
          {
            regex: "_mail",
            by: "",
          },
        ],
      },
    ],
    links: [
      {
        url: "https://somewhere",
        name: "Some Name",
        extension: "pdf",
      },
    ],
  };

  const initialConfig: any = {
    when: automation_config_defaults?.[automation!]?.[action!]?.when?.value || "",
    active: false,
    same_body_as: "",
    bcc: "",
    custom_trigger: "",
    from_name: "",
    from_email: "",
    to_email: "",
    testing: "",
    being_tested: false,
    test_phone: "",
    fileAttachmentConfig: null,
  };
  const [config, setConfig] = useState<any | undefined>(initialConfig);
  const [configOnOpenPage, setConfigOnOpenPage] = useState<any | "">({});

  // Since we don't wanna update the BCC and custom_trigger fields on each change (as this would rerender the component and
  // change the focus to the beginning of the field on each letter type), we need a workaround to force update the component
  // (as switching between two flow mails, keeps showing the same TemplateConfig component)
  const [forceRerenderKey, setForceRerenderKey] = useState<number>(Date.now());

  let [errors, setErrors] = useState<any>({});

  // Get our mailTemplate Configs (they should be already fetched by the loader, hence the staleTime)
  const { data: templateConfig } = useQuery({
    queryKey: ["mailTemplates", "configs", mailTemplatePath],
    queryFn: ({ signal }) => fetchMailTemplateConfig({ signal, mailTemplatePath }),
    staleTime: 2000,
  });

  useEffect(() => {
    if (Object.keys(templateConfig).length === 0) {
      setConfig({ ...initialConfig });
      setConfigOnOpenPage({ ...initialConfig });
      return;
    }

    let configOnStart = {
      ...templateConfig,
      bcc: templateConfig.bcc ? JSON.stringify(templateConfig.bcc || "", undefined, 3) : "",
      fileAttachmentConfig: templateConfig.fileAttachmentConfig
        ? JSON.stringify(templateConfig.fileAttachmentConfig || "", undefined, 3)
        : "",
      custom_trigger: templateConfig.custom_trigger
        ? JSON.stringify(templateConfig.custom_trigger || "", undefined, 3)
        : "",
    };
    Object.keys(initialConfig).forEach(function (key) {
      configOnStart[key] = configOnStart[key] || initialConfig[key];
    });

    setAttachFiles(configOnStart.fileAttachmentConfig != null);

    setConfig({ ...configOnStart });
    setConfigOnOpenPage({ ...configOnStart });
    setForceRerenderKey(Date.now());
  }, [params, templateConfig]);

  // Save the config
  const { mutate: mutateSaveConfig } = useMutation({
    mutationFn: saveMailTemplateConfig,
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["mailTemplates", "summary"] });
      queryClient.invalidateQueries({ queryKey: ["mailTemplates", "configs", mailTemplatePath] });
      setSaving(false);
    },
  });

  const saveConfig = async () => {
    setSaving(true);
    let newErrors = {};
    const newConfig = {
      ...config,
      bcc: {},
      custom_trigger: {},
      fileAttachmentConfig: {},
    };
    if (config.bcc) {
      try {
        newConfig.bcc = JSON.parse(config.bcc);
      } catch (e) {
        newErrors = { ...newErrors, bcc: "An error occured during parsing, please verify JSON is correct." };
      }
    }
    if (config.custom_trigger) {
      try {
        newConfig.custom_trigger = JSON.parse(config.custom_trigger);
      } catch (e) {
        newErrors = { ...newErrors, custom_trigger: "An error occured during parsing, please verify JSON is correct." };
      }
    }
    if (config.fileAttachmentConfig) {
      try {
        newConfig.fileAttachmentConfig = JSON.parse(config.fileAttachmentConfig);
      } catch (e) {
        newErrors = {
          ...newErrors,
          fileAttachmentConfig: "An error occured during parsing, please verify JSON is correct.",
        };
      }
    }
    if (
      config.active &&
      (!config.same_body_as || config.same_body_as === "") &&
      templatesMaintained.filter(
        (t) => t.version === "live" && t.event === event && t.action === action && t.target === target,
      ).length === 0
    ) {
      newErrors = {
        ...newErrors,
        activateOnlyLive: "You can only activate a template if it has or refers to a live version.",
      };
    }
    setErrors(newErrors);
    setSaving(false);
    if (Object.keys(newErrors).length > 0) {
      return;
    }
    await mutateSaveConfig({ mailTemplatePath, config: newConfig });
  };

  const showError = (field: string) => {
    if (field in errors) {
      return (
        <Alert className="alert alert-danger text-danger">
          <i className="bi bi-exclamation-octagon-fill"></i> {errors[field]}
        </Alert>
      );
    }
  };

  return (
    <Row className="rounded p-2 mb-3" style={{ backgroundColor: "#d6d9f0" }}>
      <h5 className="my-2 mb-4">
        Config {`${event!.toUpperCase()} > ${action!.toUpperCase()} > ${target!.toUpperCase()}`}
      </h5>
      {activeCompany!.name !== "template" && (
        <div className="text-danger mb-4">
          <i className="bi bi-exclamation-octagon-fill "></i> Be careful, changing this configuration will be effective
          immediately upon the next job run.
        </div>
      )}
      {showError("activateOnlyLive")}
      <Container>
        <Row>
          <Col xs="6">
            {event !== "MARIAGE" && (
              <Form.Group as={Row} className="mb-3 align-items-center">
                <Form.Label column sm="4">
                  When
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Tooltip id={`tooltip-bottom`}>
                        When should this mail be triggered? It should be of the format days=-1 if the mail should e.g.
                        be triggered one day before the event. Note: some actions currently don't take this into
                        account.
                      </Tooltip>
                    }
                  >
                    <i className="bi bi-info-circle ms-1"></i>
                  </OverlayTrigger>
                </Form.Label>
                <Col sm="8">
                  <Form.Control
                    plaintext
                    id="when"
                    value={config?.when}
                    style={{ border: "solid #999999", padding: "10px" }}
                    onChange={(e) => {
                      setConfig({ ...config, when: e.target.value });
                    }}
                    readOnly={
                      automation_config_defaults?.[automation!]?.[action!]?.when?.displayOnly ||
                      activeCompany!.name === "template"
                    }
                    placeholder={automation_config_defaults?.[automation!]?.[action!]?.when?.placeholder}
                  />
                </Col>
              </Form.Group>
            )}
          </Col>
          {activeCompany!.name !== "template" && (
            <Col>
              <Form.Group as={Row} className="mb-3 align-items-center">
                <Form.Label column sm="5" className="text-end">
                  Active
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Tooltip id={`tooltip-bottom`}>
                        Should we send out mails for {event} &gt; {action} &gt; {target}?
                      </Tooltip>
                    }
                  >
                    <i className="bi bi-info-circle ms-1"></i>
                  </OverlayTrigger>
                </Form.Label>
                <Col sm="2">
                  <Form.Check
                    type="switch"
                    checked={config?.active}
                    disabled={activeCompany?.name === "cornelisenpartners"}
                    onChange={() => {
                      setConfig({ ...config, active: !config?.active });
                    }}
                  />
                </Col>
                <Col sm="5" className="text-end">
                  <Button
                    className={`p-2 m-1 btn-sm ${saving ? "btn-secondary" : "btn-primary"}`}
                    onClick={saveConfig}
                    disabled={activeCompany?.name === "cornelisenpartners"}
                  >
                    {saving && "Saving..."}
                    {!saving && "Save Config"}
                  </Button>
                </Col>
              </Form.Group>
            </Col>
          )}
        </Row>
        {event !== "MARIAGE" && (
          <Row>
            <Col>
              <Form.Group as={Row} className="mb-3 align-items-center">
                <Form.Label column sm="2">
                  Same body as
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Tooltip id={`tooltip-bottom`}>
                        If this mail has the same body as another mail, indicate it here.
                      </Tooltip>
                    }
                  >
                    <i className="bi bi-info-circle ms-1"></i>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    value={config?.same_body_as}
                    onChange={(e) => {
                      setConfig({ ...config, same_body_as: e.target.value });
                    }}
                    disabled={activeCompany?.name === "cornelisenpartners"}
                    style={{ border: "solid #999999", padding: "10px", backgroundColor: "#D6D9F0" }}
                  >
                    const liveVersions =<option value=""></option>
                    {templatesMaintained
                      .filter((t) => t.version == "live" && t.event != "replacements")
                      .reduce((accumulator, currentValue) => {
                        if (
                          accumulator.filter(
                            (t: any) =>
                              t.event == currentValue.event &&
                              t.action == currentValue.action &&
                              t.target == currentValue.target,
                          ).length == 0
                        ) {
                          accumulator.push(currentValue);
                        }
                        return accumulator;
                      }, [])
                      .map((t: any) => {
                        return (
                          <option
                            key={`${t.event}.${t.action}.${t.target}`}
                            value={`${t.event}.${t.action}.${t.target}`}
                          >
                            {`${t.automation} > ${t.event} > ${t.action} > ${t.target}`}
                          </option>
                        );
                      })}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>
        )}
        <Row>
          <Col xs="6">
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm="4">
                From Name
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Tooltip id={`tooltip-bottom`}>
                      What should be the sender name? Only provide if it should deviate from the default.
                    </Tooltip>
                  }
                >
                  <i className="bi bi-info-circle ms-1"></i>
                </OverlayTrigger>
              </Form.Label>
              <Col sm="8">
                <Form.Control
                  plaintext
                  id="from_name"
                  value={config?.from_name}
                  disabled={activeCompany?.name === "cornelisenpartners"}
                  style={{ border: "solid #999999", padding: "10px" }}
                  onChange={(e) => {
                    setConfig({ ...config, from_name: e.target.value });
                  }}
                  placeholder="Leave empty if it does not apply"
                />
              </Col>
            </Form.Group>
          </Col>
          <Col>
            {action !== "reminder_sms" && (
              <Form.Group as={Row} className="mb-3 align-items-center">
                <Form.Label column sm="5" className="text-end">
                  Testing
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Tooltip id={`tooltip-bottom`}>
                        Do you want to override the testing status on automation level? If so, is it in customer or
                        Colibry testing?
                      </Tooltip>
                    }
                  >
                    <i className="bi bi-info-circle ms-1"></i>
                  </OverlayTrigger>
                </Form.Label>
                <Col sm="7">
                  <Form.Select
                    value={config?.testing}
                    onChange={(e) => {
                      setConfig({ ...config, testing: e.target.value === "not" ? null : e.target.value });
                    }}
                    disabled={activeCompany?.name === "cornelisenpartners"}
                    style={{ border: "solid #999999", padding: "10px", backgroundColor: "#D6D9F0" }}
                  >
                    <option key="test-not" value="not">
                      - (no override)
                    </option>
                    <option key="test-colibry" value="colibry">
                      Colibry (Internal)
                    </option>
                    <option key="test-customer" value="customer">
                      Customer (External)
                    </option>
                  </Form.Select>
                </Col>
              </Form.Group>
            )}

            {action === "reminder_sms" && (
              <Form.Group as={Row} className="mb-3 align-items-center">
                <Form.Label column sm="5" className="text-end">
                  Being Tested
                  <OverlayTrigger
                    placement="bottom"
                    overlay={<Tooltip id={`tooltip-bottom`}>Is this reminder SMS currently being tested?</Tooltip>}
                  >
                    <i className="bi bi-info-circle ms-1"></i>
                  </OverlayTrigger>
                </Form.Label>
                <Col sm="7">
                  <Form.Check
                    type="switch"
                    checked={config?.being_tested}
                    disabled={activeCompany?.name === "cornelisenpartners"}
                    onChange={() => {
                      setConfig({ ...config, being_tested: !config?.being_tested });
                    }}
                  />
                </Col>
              </Form.Group>
            )}
          </Col>
        </Row>
        <Row>
          <Col xs="6">
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm="4">
                From Email
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Tooltip id={`tooltip-bottom`}>
                      What should be the sender email? Only provide if it should deviate from the default. It should be
                      an email address that is known as a sender in DotDigital, using the <u>em</u> domain.
                    </Tooltip>
                  }
                >
                  <i className="bi bi-info-circle ms-1"></i>
                </OverlayTrigger>
              </Form.Label>
              <Col sm="8">
                <Form.Control
                  plaintext
                  id="from_email"
                  disabled={activeCompany?.name === "cornelisenpartners"}
                  value={config?.from_email}
                  style={{ border: "solid #999999", padding: "10px" }}
                  onChange={(e) => {
                    setConfig({ ...config, from_email: e.target.value });
                  }}
                  placeholder="Leave empty if it does not apply"
                />
              </Col>
            </Form.Group>
          </Col>
          <Col>
            {action === "reminder_sms" && config?.being_tested && (
              <Form.Group as={Row} className="mb-3 align-items-center">
                <Form.Label column sm="5" className="text-end">
                  Test Phone
                  <OverlayTrigger
                    placement="bottom"
                    overlay={<Tooltip id={`tooltip-bottom`}>To which phone should the test SMS messages go?</Tooltip>}
                  >
                    <i className="bi bi-info-circle ms-1"></i>
                  </OverlayTrigger>
                </Form.Label>
                <Col sm="7">
                  <Form.Control
                    plaintext
                    disabled={activeCompany?.name === "cornelisenpartners"}
                    id="test_phone"
                    value={config?.test_phone}
                    style={{ border: "solid #999999", padding: "10px" }}
                    onChange={(e) => {
                      setConfig({ ...config, test_phone: e.target.value });
                    }}
                  />
                </Col>
              </Form.Group>
            )}
          </Col>
        </Row>
        {((automation === "LISTING_LIVE" && target === "representative") ||
          automation === "ACTIVITY_REPORT" ||
          automation === "CANCELLATION") && (
          <Row>
            <Col xs="6">
              <Form.Group as={Row} className="mb-3 align-items-center">
                <Form.Label column sm="4">
                  To Email
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Tooltip id={`tooltip-bottom`}>
                        Provides the possibility to overwrite the address to which we will send the mail
                      </Tooltip>
                    }
                  >
                    <i className="bi bi-info-circle ms-1"></i>
                  </OverlayTrigger>
                </Form.Label>
                <Col sm="8">
                  <Form.Control
                    plaintext
                    id="to_email"
                    disabled={activeCompany?.name === "cornelisenpartners"}
                    value={config?.to_email}
                    style={{ border: "solid #999999", padding: "10px" }}
                    onChange={(e) => {
                      setConfig({ ...config, to_email: e.target.value });
                    }}
                    placeholder="Leave empty if it does not apply"
                  />
                </Col>
              </Form.Group>
            </Col>
            <Col></Col>
          </Row>
        )}
        {activeCompany!.name !== "template" && event !== "MARIAGE" && action !== "reminder_sms" && (
          <>
            <Row>
              <Col xs="2">
                BCC mails
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip id={`tooltip-bottom`}>JSON for BCC mails that need to go out.</Tooltip>}
                >
                  <i className="bi bi-info-circle ms-1"></i>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip id={`tooltip-bottom`}>Prefill the BCC field.</Tooltip>}
                >
                  <i
                    className="bi bi-magic ms-2"
                    onClick={() => {
                      setConfigOnOpenPage({ ...configOnOpenPage, bcc: JSON.stringify(emptyBCC, undefined, 3) });
                    }}
                  ></i>
                </OverlayTrigger>
              </Col>
              <Col xs="10">
                {showError("bcc")}
                <Editor
                  key={`bcc_${forceRerenderKey}`}
                  language="json"
                  value={configOnOpenPage.bcc}
                  onUpdate={(text) => {
                    setConfig({ ...config, bcc: text });
                  }}
                  wordWrap={true}
                >
                  {(editor) => <BasicSetup editor={editor} />}
                </Editor>
              </Col>
            </Row>
            <Row className="pt-3">
              <Col xs="2">
                Custom Trigger
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip id={`tooltip-bottom`}>JSON for custom triggers.</Tooltip>}
                >
                  <i className="bi bi-info-circle ms-1"></i>
                </OverlayTrigger>
              </Col>
              <Col xs="10">
                {showError("custom_trigger")}
                <Editor
                  key={`custom_trigger_${forceRerenderKey}`}
                  language="json"
                  value={configOnOpenPage.custom_trigger}
                  onUpdate={(text) => {
                    setConfig({ ...config, custom_trigger: text });
                  }}
                  wordWrap={true}
                >
                  {(editor) => <BasicSetup editor={editor} />}
                </Editor>
              </Col>
            </Row>
            {!["CANCELLATION", "INVALID_DATA", "MARIAGE"].includes(automation!) && (
              <Row className="pt-3">
                <Col xs="2">
                  Attach files
                  <OverlayTrigger
                    placement="bottom"
                    overlay={<Tooltip id={`tooltip-bottom`}>Attach files to mails.</Tooltip>}
                  >
                    <i className="bi bi-info-circle ms-1"></i>
                  </OverlayTrigger>
                  <OverlayTrigger
                    placement="bottom"
                    overlay={<Tooltip id={`tooltip-bottom`}>Prefill the attach file config with an example.</Tooltip>}
                  >
                    <i
                      className="bi bi-magic ms-2"
                      onClick={() => {
                        setConfigOnOpenPage({
                          ...configOnOpenPage,
                          fileAttachmentConfig: JSON.stringify(emptyFileAttachmentConfig, undefined, 3),
                        });
                        setAttachFiles(true);
                      }}
                    ></i>
                  </OverlayTrigger>
                </Col>
                <Col xs="10">
                  {showError("fileAttachmentConfig")}
                  <div className="mb-3">
                    <Container>
                      <Row>
                        <Col sm={1}>
                          <Form.Check
                            type="switch"
                            checked={attachFiles}
                            onChange={() => {
                              // Remove file attachment config if we switch it off
                              if (attachFiles) {
                                setConfig({ ...config, fileAttachmentConfig: null });
                              }
                              setAttachFiles(!attachFiles);
                            }}
                          />
                        </Col>
                        <Col sm={11}>{attachFiles && <span>Be careful when maintaining the below JSON.</span>}</Col>
                      </Row>
                    </Container>
                  </div>
                  {attachFiles && (
                    <Editor
                      key={`file_attachment_config_${forceRerenderKey}`}
                      language="json"
                      value={configOnOpenPage.fileAttachmentConfig}
                      onUpdate={(text) => {
                        setConfig({ ...config, fileAttachmentConfig: text });
                      }}
                      wordWrap={true}
                    >
                      {(editor) => <BasicSetup editor={editor} />}
                    </Editor>
                  )}
                </Col>
              </Row>
            )}
          </>
        )}
      </Container>
    </Row>
  );
}

export default TemplateConfig;
